import { Skeleton } from '@/_components/ui/skeleton'
import type { WithAuthComponentProps } from '@/_hocs/with-auth'
import { WithAuth } from '@/_hocs/with-auth'
import { ContentContainer } from '@/features/_components/content-container'
import { SkeletonFormActions } from '@/features/_components/skeletons/skeleton-form-actions'
import { SkeletonInputGroup } from '@/features/_components/skeletons/skeleton-input-group'
import { Container } from '@/features/_components/ui/container'
import { useApplicationParams } from '@/features/_hooks/use-application-params'
import { queries } from '@/features/_lib/react-query'
import { useQuery } from '@tanstack/react-query'
import { PersonHeader } from '../_components/person-header'
import { PersonHeaderSkeleton } from '../_components/person-header.skeleton'
import { EditProvider } from '../_providers/edit'
import { ReleaseHandoffCard } from './_components/release-handoff-card'
import { UpdatePersonForm } from './_components/update-person-form'
import { FormProvider } from './_providers/form'

const ProfileFormSkeleton = () => (
  <div className="flex min-h-0 w-full flex-1 flex-col">
    <div className="flex flex-1 flex-col overflow-hidden">
      <div className="scrollbar flex-1 overflow-y-auto">
        <div className="flex gap-5">
          <div className="flex w-full flex-col gap-5">
            <ContentContainer>
              <Skeleton className="h-5 w-32" />
              <SkeletonInputGroup fields={2} />
              <SkeletonInputGroup fields={2} />
              <SkeletonInputGroup fields={2} />
            </ContentContainer>
          </div>
          <div className="flex w-[30%] shrink-0 flex-col gap-[10px]">
            <ContentContainer className="overflow-y-auto">
              <Skeleton className="h-5 w-40" />
              <div className="space-y-5">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <Skeleton className="h-8 w-8 shrink-0 rounded-full" />
                    <div className="flex flex-col gap-1">
                      <Skeleton className="h-4 w-28" />
                      <Skeleton className="h-3 w-20" />
                    </div>
                  </div>
                ))}
              </div>
            </ContentContainer>
          </div>
        </div>
      </div>
      <SkeletonFormActions />
    </div>
  </div>
)

const ProfilePage = ({ session }: WithAuthComponentProps) => {
  const { companyId, facilityId, applicationId } = useApplicationParams()

  const { data, isLoading } = useQuery({
    ...queries.applications.detailFull(companyId, facilityId, applicationId),
    enabled: !!companyId && !!facilityId,
  })

  return (
    <Container className="h-full overflow-hidden px-14 py-10 gap-6">
      {isLoading || !data ? (
        <>
          <PersonHeaderSkeleton />
          <ProfileFormSkeleton />
        </>
      ) : (
        <EditProvider>
          <FormProvider
            person={data.application.person}
            application={data.application}
            facility={data.facility}
          >
            <PersonHeader
              application={data.application}
              person={data.application.person}
              facility={data.facility}
              userType={session.user.userType}
            />
            <ReleaseHandoffCard application={data.application} />
            <UpdatePersonForm
              application={data.application}
              user={session.user}
              userType={session.user.userType}
            />
          </FormProvider>
        </EditProvider>
      )}
    </Container>
  )
}

export default WithAuth(ProfilePage, { allowedFor: ['staff', 'tenant'] })
